import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@immo/ui-kit';
import type { AdminMeResponse, Permission } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { env } from '@/lib/env';
import { PHONE_QUERY, useMediaQuery } from '@/hooks/use-media-query';
import { navItems, type NavChildItem, type NavItem } from './nav-items';
import { UserMenu } from './user-menu';

type Props = {
  readonly me: AdminMeResponse;
  /** Phone only: whether the nav drawer is open. Ignored above `sm`. */
  readonly open?: boolean;
  readonly onClose?: () => void;
};

const COLLAPSE_QUERY = '(max-width: 900px)';

/**
 * Three shapes, one nav.
 *
 * Above 900px: the full sidebar. Between `sm` and 900px: the 56px icon rail,
 * where a flyout carries the child routes. Below `sm`: a DRAWER — off-canvas
 * until the topbar's menu button opens it.
 *
 * The rail is the wrong answer on a phone and the fleet page is where it shows:
 * 56px of a 390px screen is a seventh of the width, permanently, spent on
 * icons whose labels only a hover can reveal — and hover is not a thing a
 * phone has. The drawer gives that width back and shows the labels, because a
 * drawer has room for them (IMM-910).
 */
export function Sidebar({ me, open = false, onClose }: Props) {
  const t = useTranslations('pages.admin.shell.nav');
  const phone = useMediaQuery(PHONE_QUERY);
  // The rail is the between-size answer only. On a phone the drawer is wide
  // enough for labels, so collapsing it there would hide them for nothing.
  const collapsed = useMediaQuery(COLLAPSE_QUERY) && !phone;
  const grantedPermissions = new Set<string>(me.user.permissions);
  const visibleNavItems = visibleItemsFor(grantedPermissions);

  const aside = (
    <aside
      // Following a link inside the drawer has to close it, or the page behind
      // changes while the nav still covers it. Delegated rather than threaded
      // through four components as a prop nobody above `sm` would ever use.
      onClick={
        phone
          ? (event) => {
              if ((event.target as Element).closest('a') !== null) onClose?.();
            }
          : undefined
      }
      className={cn(
        'flex h-screen flex-col border-r border-border bg-canvas',
        phone
          ? cn(
              'fixed inset-y-0 left-0 z-50 w-sidebar shadow-xl transition-transform duration-fast ease-out',
              open ? 'translate-x-0' : '-translate-x-full',
            )
          : cn('shrink-0', collapsed ? 'w-14' : 'w-sidebar'),
      )}
      aria-hidden={phone && !open}
      // A closed drawer is off-canvas, not gone: without `inert` its links stay
      // in the tab order and focus walks into a nav nobody can see.
      inert={phone && !open}
    >
      <div
        className={cn(
          'flex h-topbar items-center border-b border-border',
          collapsed ? 'justify-center px-0' : 'gap-2 px-4',
        )}
      >
        <span
          aria-hidden
          className="flex h-7 w-7 items-center justify-center rounded-md bg-interactive text-base font-semibold text-interactive-on"
        >
          /
        </span>
        {!collapsed && <span className="text-base font-semibold tracking-tight text-text-primary">immoOS Admin</span>}
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto p-2">
        {visibleNavItems.map((item) => (
          <NavGroup key={item.path} item={item} translate={t} collapsed={collapsed} />
        ))}
      </nav>
      <div className="border-t border-border p-2">
        <UserMenu me={me} collapsed={collapsed} />
      </div>
    </aside>
  );

  if (!phone) return aside;

  return (
    <>
      {open && (
        <div
          // A backdrop, not a close button: tapping beside a drawer is how a
          // drawer closes. It renders only while open so it never swallows a
          // tap on the page behind it.
          aria-hidden
          onClick={onClose}
          className="fixed inset-0 z-40 bg-overlay"
        />
      )}
      {aside}
    </>
  );
}

/**
 * Nav visibility, resolved in one place so the expanded list and the collapsed
 * rail can never disagree. A group survives when its own permission passes and,
 * if it declares children, at least one child survives — derived rather than
 * declared, so a group can't drift out of step with what it actually shows.
 */
export function visibleItemsFor(
  grantedPermissions: ReadonlySet<string>,
  sandbox: boolean = env.sandbox,
): ReadonlyArray<NavItem> {
  const holds = (permission?: Permission) => permission === undefined || grantedPermissions.has(permission);

  return navItems
    .filter((item) => !(sandbox && item.productionOnly))
    .filter((item) => holds(item.permission))
    .map((item) =>
      item.children === undefined
        ? item
        : { ...item, children: item.children.filter((child) => holds(child.permission)) },
    )
    .filter((item) => item.children === undefined || item.children.length > 0);
}

type Translate = (key: string) => string;

const baseNavClass =
  'relative flex h-sidebar-item items-center gap-2 rounded-md px-3 text-base-sm text-text-secondary transition-colors duration-micro ease-out hover:bg-hover-bg hover:text-text-primary';
const collapsedNavClass =
  'relative flex h-sidebar-item items-center justify-center rounded-md text-text-secondary transition-colors duration-micro ease-out hover:bg-hover-bg hover:text-text-primary focus-visible:outline-none focus-visible:shadow-focus';
const activeNavClass = 'bg-selected-bg font-semibold text-text-primary';

function ActiveMark() {
  return <span aria-hidden className="absolute left-0 top-0 h-full w-0.5 bg-interactive" />;
}

const tooltipClass =
  'pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-tooltip px-2 py-1 text-sm text-neutral-0 opacity-0 shadow-md transition-opacity duration-micro ease-out group-hover:opacity-100 group-focus-within:opacity-100';

// Collapsed groups have no room for their child links inline, so the icon
// opens a flyout with the section overview plus its children — otherwise
// the nested routes are unreachable on the rail. Rendered in a portal so
// the narrow, overflow-clipped rail doesn't cut it off.
function CollapsedNavGroup({ item, label, translate }: { item: NavItem; label: string; translate: Translate }) {
  const children = item.children ?? [];
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const active = pathname === item.path || pathname.startsWith(`${item.path}/`);

  useLayoutEffect(() => {
    if (!open || triggerRef.current === null) return;

    const rect = triggerRef.current.getBoundingClientRect();

    setPosition({ top: rect.top, left: rect.right + 8 });
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = triggerRef.current?.contains(target) ?? false;
      const inMenu = menuRef.current?.contains(target) ?? false;

      if (!inTrigger && !inMenu) setOpen(false);
    };

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const itemClass = (isActive: boolean) =>
    cn(
      'block px-3 py-2 text-base-sm text-text-secondary transition-colors duration-micro ease-out hover:bg-hover-bg hover:text-text-primary',
      isActive && 'bg-selected-bg font-semibold text-text-primary',
    );

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={label}
        aria-haspopup="true"
        aria-expanded={open}
        aria-current={active ? 'page' : undefined}
        className={cn(collapsedNavClass, 'w-full', active && activeNavClass)}
      >
        {active && <ActiveMark />}
        <item.icon className="h-4 w-4" aria-hidden />
      </button>
      {open &&
        position !== null &&
        createPortal(
          <div
            ref={menuRef}
            style={{ top: position.top, left: position.left }}
            className="fixed z-50 min-w-[180px] overflow-hidden rounded-lg border border-border bg-surface py-1 shadow-md"
          >
            <NavLink
              to={item.path}
              end
              onClick={() => setOpen(false)}
              className={({ isActive }) => cn(itemClass(isActive), 'font-semibold')}
            >
              {label}
            </NavLink>
            {children.map((child) => (
              <NavLink
                key={child.path}
                to={child.path}
                end
                onClick={() => setOpen(false)}
                className={({ isActive }) => itemClass(isActive)}
              >
                {translate(child.labelKey)}
              </NavLink>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

function NavGroup({ item, translate, collapsed }: { item: NavItem; translate: Translate; collapsed: boolean }) {
  const label = translate(item.labelKey);

  if (collapsed) {
    if (item.children === undefined) {
      return (
        <div className="group relative">
          <NavLink
            to={item.path}
            end
            aria-label={label}
            className={({ isActive }) => cn(collapsedNavClass, isActive && activeNavClass)}
          >
            {({ isActive }) => (
              <>
                {isActive && <ActiveMark />}
                <item.icon className="h-4 w-4" aria-hidden />
              </>
            )}
          </NavLink>
          <span role="tooltip" className={tooltipClass}>
            {label}
          </span>
        </div>
      );
    }

    return <CollapsedNavGroup item={item} label={label} translate={translate} />;
  }

  return (
    <div className="space-y-1">
      <NavLink to={item.path} end className={({ isActive }) => cn(baseNavClass, isActive && activeNavClass)}>
        {({ isActive }) => (
          <>
            {isActive && <ActiveMark />}
            <item.icon className="h-4 w-4" aria-hidden />
            <span>{label}</span>
          </>
        )}
      </NavLink>
      {item.children !== undefined && (
        <div className="space-y-1 pl-6">
          {item.children.map((child) => (
            <NavChild key={child.path} item={child} translate={translate} />
          ))}
        </div>
      )}
    </div>
  );
}

function NavChild({ item, translate }: { item: NavChildItem; translate: Translate }) {
  return (
    <NavLink to={item.path} end className={({ isActive }) => cn(baseNavClass, isActive && activeNavClass)}>
      {({ isActive }) => (
        <>
          {isActive && <ActiveMark />}
          <span>{translate(item.labelKey)}</span>
        </>
      )}
    </NavLink>
  );
}
